import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { CurrentRestaurantService } from './current-restaurant.service';
import { JwtService } from './jwt.service';
import { PrepSoundSettings, resolvePrepSoundSettings } from './kitchen-settings.service';
import { KitchenSoundService } from './kitchen-sound.service';
import { PermissionsService } from './permissions.service';
import {
  PreparationStatus,
  PreparationTicketResponse,
  PreparationTicketsService,
} from './preparation-tickets.service';
import { PrintingSettingsService } from './printing-settings.service';
import { TranslationService } from './translation.service';
import { UserAlertPreferencesService } from './user-alert-preferences.service';

export const PREPARATION_ROUTE = '/order-preparation';

const POLL_INTERVAL_MS = 12_000;
// The setting is managed by someone else: a session left open must follow it without a reload.
const PREFERENCE_TTL_MS = 5 * 60_000;
const TOAST_DURATION_MS = 10_000;
const MAX_LISTED_TICKETS = 3;
const SYSTEM_NOTIFICATION_TAG = 'yz-prep-arrival';
const TOAST_CLOSE_SELECTOR = '.ant-notification-notice-close';

@Injectable({ providedIn: 'root' })
export class PrepAwayAlertService {
  private readonly router = inject(Router);
  private readonly jwt = inject(JwtService);
  private readonly currentRestaurant = inject(CurrentRestaurantService);
  private readonly permissions = inject(PermissionsService);
  private readonly preferences = inject(UserAlertPreferencesService);
  private readonly tickets = inject(PreparationTicketsService);
  private readonly restaurantSettings = inject(PrintingSettingsService);
  private readonly kitchenSound = inject(KitchenSoundService);
  private readonly toast = inject(NzNotificationService);
  private readonly translation = inject(TranslationService);

  readonly enabled = signal(false);
  readonly stationIds = signal<string[]>([]);

  private timer: ReturnType<typeof setInterval> | null = null;
  private loadedRestaurantId: string | null = null;
  private loadedAt = 0;
  private permissionRequestArmed = false;
  private soundSettings: PrepSoundSettings | null = null;
  private knownPendingIds: Set<string> | null = null;
  private pendingForAccount = false;
  private reminderTimer: ReturnType<typeof setInterval> | null = null;
  private reminderSeconds = 0;

  start(): void {
    if (this.timer) {
      return;
    }
    void this.poll();
    this.timer = setInterval(() => void this.poll(), POLL_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.forget();
  }

  /** Picks up a setting changed from the kitchen settings without waiting for the next periodic reload. */
  async refresh(): Promise<void> {
    this.loadedRestaurantId = null;
    await this.poll();
  }

  async poll(): Promise<void> {
    const restaurantId = this.currentRestaurant.currentId();
    if (!restaurantId || !this.jwt.isLogged()) {
      this.forget();
      return;
    }
    if (
      this.loadedRestaurantId !== restaurantId ||
      Date.now() - this.loadedAt > PREFERENCE_TTL_MS
    ) {
      await this.loadPreference(restaurantId);
    }
    // On the board itself the page rings on its own; dropping the baseline keeps the tickets seen there from ringing on the way out.
    if (!this.enabled() || !this.permissions.can('ORDER', 'PREPARE') || this.onPreparationPage()) {
      this.knownPendingIds = null;
      this.pendingForAccount = false;
      return;
    }

    let pending: PreparationTicketResponse[];
    try {
      pending = await firstValueFrom(
        this.tickets.getTickets({ restaurantId, status: PreparationStatus.Pending })
      );
    } catch {
      return;
    }

    const stations = this.stationIds();
    const mine = pending.filter(
      (ticket) => stations.length === 0 || stations.includes(ticket.functionalCategoryId)
    );
    this.pendingForAccount = mine.length > 0;

    const previous = this.knownPendingIds;
    this.knownPendingIds = new Set(pending.map((ticket) => ticket._id));
    if (!previous) {
      return;
    }
    const arrived = mine.filter((ticket) => !previous.has(ticket._id));
    if (arrived.length > 0) {
      this.alert(arrived);
    }
  }

  protected isDocumentHidden(): boolean {
    return document.visibilityState === 'hidden';
  }

  protected needsSystemPermission(): boolean {
    return typeof Notification !== 'undefined' && Notification.permission === 'default';
  }

  protected requestSystemPermission(): void {
    if (!this.needsSystemPermission()) {
      return;
    }
    try {
      void Notification.requestPermission()?.catch(() => undefined);
    } catch {
      // Safari < 16 only takes a callback and throws on the promise form.
    }
  }

  protected async showSystemNotification(title: string, body: string): Promise<void> {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }
    const options = {
      body,
      tag: SYSTEM_NOTIFICATION_TAG,
      renotify: true,
      data: {
        onActionClick: {
          default: { operation: 'navigateLastFocusedOrOpen', url: PREPARATION_ROUTE },
        },
      },
    } as NotificationOptions;
    try {
      // Android Chrome refuses `new Notification()`: the service worker has to show it.
      const registration = await navigator.serviceWorker?.getRegistration();
      if (registration) {
        await registration.showNotification(title, options);
        return;
      }
      const notification = new Notification(title, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
        void this.router.navigateByUrl(PREPARATION_ROUTE);
      };
    } catch {
      // The in-app toast and the sound already carried the alert.
    }
  }

  private alert(arrived: PreparationTicketResponse[]): void {
    this.ring();
    // Restarted from the arrival so the reminder never lands right behind it.
    this.scheduleReminder(true);

    const title = this.translation.get('orderPreparation.away.title');
    const body = this.describe(arrived);
    const toast = this.toast.info(title, body, { nzDuration: TOAST_DURATION_MS });
    toast.onClick.subscribe((event) => {
      if ((event.target as HTMLElement | null)?.closest?.(TOAST_CLOSE_SELECTOR)) {
        return;
      }
      this.toast.remove(toast.messageId);
      void this.router.navigateByUrl(PREPARATION_ROUTE);
    });

    if (this.isDocumentHidden()) {
      void this.showSystemNotification(title, body);
    }
  }

  private describe(arrived: PreparationTicketResponse[]): string {
    const takeaway = this.translation.get('orderPreparation.takeaway');
    const labels = arrived
      .slice(0, MAX_LISTED_TICKETS)
      .map((ticket) => `${ticket.tableLabel || takeaway} · ${ticket.functionalCategoryLabel}`);
    const hidden = arrived.length - labels.length;
    if (hidden > 0) {
      labels.push(`+${hidden}`);
    }
    return labels.join(', ');
  }

  private onPreparationPage(): boolean {
    return this.router.url.split(/[?#]/)[0] === PREPARATION_ROUTE;
  }

  private async loadPreference(restaurantId: string): Promise<void> {
    if (this.loadedRestaurantId !== restaurantId) {
      this.knownPendingIds = null;
    }
    try {
      const preferences = await firstValueFrom(this.preferences.getMine(restaurantId));
      this.enabled.set(preferences?.orderPreparation?.awayFromPage === true);
      this.stationIds.set(preferences?.orderPreparation?.functionalCategoryIds ?? []);
      this.loadedRestaurantId = restaurantId;
      this.loadedAt = Date.now();
    } catch {
      // Keep the last known setting: a failed reload must not silence someone who is alerted.
      return;
    }
    if (this.enabled()) {
      this.armSystemPermissionRequest();
      await this.loadSoundSettings(restaurantId);
    }
  }

  // The setting is turned on by a manager, so there is no click of the user's own to hang the prompt on: take the next one.
  private armSystemPermissionRequest(): void {
    if (this.permissionRequestArmed || !this.needsSystemPermission()) {
      return;
    }
    this.permissionRequestArmed = true;
    const request = () => {
      document.removeEventListener('pointerdown', request);
      this.permissionRequestArmed = false;
      if (this.enabled()) {
        this.requestSystemPermission();
      }
    };
    document.addEventListener('pointerdown', request);
  }

  private ring(): void {
    const settings = this.soundSettings;
    if (settings?.prepSoundEnabled && !this.kitchenSound.muted()) {
      this.kitchenSound.play(settings.prepSound, settings.prepSoundVolume);
    }
  }

  private scheduleReminder(restart = false): void {
    const settings = this.soundSettings;
    const seconds = settings?.prepSoundEnabled ? settings.prepSoundReminderSeconds : 0;
    if (!restart && seconds === this.reminderSeconds && (this.reminderTimer || seconds <= 0)) {
      return;
    }
    this.clearReminder();
    this.reminderSeconds = seconds;
    if (seconds > 0) {
      this.reminderTimer = setInterval(() => this.remind(), seconds * 1000);
    }
  }

  // Sound only: the toast of the arrival is already on screen, a new one every few seconds would bury the page.
  private remind(): void {
    if (this.enabled() && this.pendingForAccount && !this.onPreparationPage()) {
      this.ring();
    }
  }

  private clearReminder(): void {
    if (this.reminderTimer) {
      clearInterval(this.reminderTimer);
      this.reminderTimer = null;
    }
    this.reminderSeconds = 0;
  }

  private async loadSoundSettings(restaurantId: string): Promise<void> {
    try {
      const restaurant = await firstValueFrom(this.restaurantSettings.get(restaurantId));
      this.soundSettings = resolvePrepSoundSettings(restaurant as Partial<PrepSoundSettings>);
    } catch {
      return;
    }
    if (this.soundSettings.prepSoundEnabled) {
      this.kitchenSound.prepare();
    }
    this.scheduleReminder();
  }

  private forget(): void {
    this.enabled.set(false);
    this.stationIds.set([]);
    this.loadedRestaurantId = null;
    this.loadedAt = 0;
    this.soundSettings = null;
    this.knownPendingIds = null;
    this.pendingForAccount = false;
    this.clearReminder();
  }
}
